import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { MarketingNav, MarketingFooter } from '../components/MarketingLayout';
import { ArrowRight, CheckCircle, Zap, Crown, Rocket, Star, Shield } from 'lucide-react';

const fadeUp = { initial: { opacity: 0, y: 24 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true }, transition: { duration: 0.6 } };

export default function PricingPage() {
  const navigate = useNavigate();

  const plans = [
    {
      name: 'Free', price: '$0', period: 'forever', color: '#64748b', icon: Zap, featured: false,
      desc: 'Get started with core career intelligence features.',
      features: [
        '1 Career Intelligence Session',
        'Basic Resume Builder',
        'Community Access (read-only)',
        'ATS Resume Score',
        'Career Progress Timeline',
      ],
    },
    {
      name: 'Pro', price: '$19', period: '/month', color: '#0D9488', icon: Crown, featured: true,
      desc: 'Unlock the full intelligence suite for serious career growth.',
      features: [
        'Unlimited Career Sessions',
        'Expert Advisory Panel',
        'All Career Tools',
        'Resume Templates (4 styles)',
        'Question Hints & Guidance',
        'Community Access (full)',
        'Grade Report Card',
        'Career Persona Discovery',
        'Priority Support',
      ],
    },
    {
      name: 'Enterprise', price: '$49', period: '/month', color: '#3B82F6', icon: Rocket, featured: false,
      desc: 'For teams and organizations that invest in their people.',
      features: [
        'Everything in Pro',
        'Team Analytics Dashboard',
        'API Access',
        'Custom Integrations',
        'Dedicated Success Manager',
        'SSO & Advanced Security',
        'Custom Assessment Batteries',
        'White-label Reports',
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-white" data-testid="pricing-page">
      <MarketingNav />

      {/* Hero */}
      <section className="pt-32 pb-8 lg:pt-40 lg:pb-12 bg-white">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <motion.div {...fadeUp}>
            <span className="text-[10px] font-bold uppercase tracking-[0.25em] block mb-3" style={{ color: '#0D9488' }}>
              Pricing
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter mb-5" style={{ color: '#0D1E3A' }}>
              Invest in <span style={{ color: '#0D9488' }}>career clarity</span>
            </h1>
            <p className="text-base lg:text-lg max-w-xl mx-auto" style={{ color: '#64748b' }}>
              Start free. Upgrade when you're ready for the full intelligence suite.
              No surprises, cancel anytime.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="py-12 lg:py-16 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {plans.map((plan, i) => {
              const PlanIcon = plan.icon;
              return (
                <motion.div key={plan.name} {...fadeUp} transition={{ delay: 0.1 + i * 0.1 }}
                  className={`rounded-2xl p-8 relative ${plan.featured ? 'ring-2 shadow-xl' : ''}`}
                  style={{
                    background: plan.featured ? '#f0fdfa' : '#fff',
                    border: `1px solid ${plan.featured ? '#0D9488' : '#e2e8f0'}`,
                    ringColor: plan.featured ? '#0D9488' : undefined,
                  }}>
                  {plan.featured && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-[10px] font-bold"
                      style={{ background: '#0D9488', color: '#fff' }}>
                      MOST POPULAR
                    </div>
                  )}

                  <div className="flex items-center gap-2 mb-3">
                    <PlanIcon className="w-5 h-5" style={{ color: plan.color }} />
                    <h3 className="text-lg font-bold" style={{ color: plan.color }}>{plan.name}</h3>
                  </div>

                  <div className="flex items-baseline gap-1 mb-2">
                    <span className="text-4xl font-extrabold" style={{ color: '#0D1E3A' }}>{plan.price}</span>
                    <span className="text-sm" style={{ color: '#94a3b8' }}>{plan.period}</span>
                  </div>

                  <p className="text-[13px] mb-6" style={{ color: '#64748b' }}>{plan.desc}</p>

                  <ul className="space-y-3 mb-8">
                    {plan.features.map(f => (
                      <li key={f} className="flex items-start gap-2.5">
                        <CheckCircle className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: plan.color }} />
                        <span className="text-sm" style={{ color: '#475569' }}>{f}</span>
                      </li>
                    ))}
                  </ul>

                  <Button onClick={() => navigate(plan.name === 'Free' ? '/register' : `/register?plan=${plan.name.toLowerCase()}`)}
                    className="w-full h-12 font-bold text-sm rounded-xl"
                    style={{
                      background: plan.featured ? '#0D9488' : '#f8fafc',
                      color: plan.featured ? '#fff' : '#475569',
                      border: plan.featured ? 'none' : '1px solid #e2e8f0',
                    }}
                    data-testid={`pricing-${plan.name.toLowerCase()}-btn`}>
                    {plan.name === 'Free' ? 'Get Started Free' : plan.name === 'Enterprise' ? 'Contact Sales' : 'Start Pro Trial'}
                    <ArrowRight className="w-4 h-4 ml-1.5" />
                  </Button>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 lg:py-24" style={{ background: '#f8fafc' }}>
        <div className="max-w-3xl mx-auto px-6">
          <motion.div {...fadeUp}>
            <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight mb-10 text-center" style={{ color: '#0D1E3A' }}>
              Frequently asked questions
            </h2>
            <div className="space-y-6">
              {[
                { q: 'Can I use Kairera for free?', a: 'Yes. The Free plan includes 1 career intelligence session, basic resume tools, and read-only community access. No credit card required.' },
                { q: 'What happens when I upgrade to Pro?', a: 'You unlock unlimited sessions, the full expert advisory panel, all career tools (decision calculator, salary simulator), premium resume templates, and full community access.' },
                { q: 'Can I cancel anytime?', a: 'Absolutely. No long-term contracts. Cancel your subscription anytime from your Settings page.' },
                { q: 'Is my data secure?', a: 'Yes. We use AES-256 encryption, are SOC 2 compliant, and give you full control over your data including GDPR-compliant export and deletion.' },
                { q: 'What makes Kairera different from other career tools?', a: 'Kairera uses structured career-science instruments, not generic personality quizzes. Our 12-dimension grading system and expert advisory panel provide depth that no other platform offers.' },
              ].map((faq, i) => (
                <div key={i} className="p-6 rounded-xl bg-white" style={{ border: '1px solid #e2e8f0' }}>
                  <h3 className="text-sm font-bold mb-2" style={{ color: '#0D1E3A' }}>{faq.q}</h3>
                  <p className="text-[13px] leading-relaxed" style={{ color: '#64748b' }}>{faq.a}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Trust */}
      <section className="py-12 bg-white">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <div className="flex flex-wrap items-center justify-center gap-6">
            {[
              { icon: Shield, label: 'SOC 2 Compliant' },
              { icon: Star, label: '4.9/5 Average Rating' },
              { icon: CheckCircle, label: '10,000+ Assessments' },
            ].map(b => (
              <div key={b.label} className="flex items-center gap-2">
                <b.icon className="w-4 h-4" style={{ color: '#0D9488' }} />
                <span className="text-xs font-semibold" style={{ color: '#475569' }}>{b.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
